import { useEffect, useRef, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import useActiveNavbar from "../hooks/useActiveNavbar";
import Login from "./Login";
import Cookies from "js-cookie";
import useAuth from "../hooks/useAuth";
import { jwtDecode } from "jwt-decode";
import { baseURL } from "../config/api";
import { appAdminSettingRoutes } from "../pages/routes/adminSettingRoutes";
import { IconButton } from "@mui/material";
import useProfile from "../hooks/useProfile";
import useAccessControl from "../hooks/useAccessControl";

export const Navbar = () => {
    const navigate = useNavigate();
    const { selected, setSelected }: any = useActiveNavbar();
    const location = useLocation();
    const { auth, setAuth }: any = useAuth();
    const { permissions }: any = useAccessControl();
    const role = Cookies.get("role-velox-charter") || "";
    const isAdmin = role.toLowerCase() === "admin";
    const { setProfileOpen }: any = useProfile();
    const token = Cookies.get("token-velox-charter");
    const profileImg = Cookies.get("profileImg-velox-charter");

    const isAdminRoute = location.pathname.startsWith("/admin");

    const [authLoaded, setAuthLoaded] = useState(false);
    const [showMenu, setShowMenu] = useState(false);
    const [openUser, setOpenUser] = useState<boolean>(false);
    const profileDropdownRef = useRef<HTMLDivElement | null>(null);
    const [openDropdown, setOpenDropdown] = useState<any | null>(null);
    const mobileViewRef = useRef<HTMLDivElement>(null);
    const dropdownRef = useRef<HTMLButtonElement[]>([]);
    const [openLogin, setOpenLogin] = useState(false);
    const [isSidebarOpen, setSidebarOpen] = useState(false);
    const [navbarHeader, setNavbarHeader] = useState<any[]>([
        {
            name: "Home",
            link: "/",
            isChildVisible: false,
            child: []
        },
        {
            name: "About Us",
            link: "/about-us",
            isChildVisible: false,
            child: []
        },
        {
            name: "Services",
            link: "/services",
            isChildVisible: false,
            child: []
        },
        {
            name: "Fleet",
            link: "/fleet",
            isChildVisible: false,
            child: []
        },
        {
            name: "FAQ",
            link: "/faq",
            isChildVisible: false,
            child: []
        },
        {
            name: "Blog",
            link: "/blog",
            isChildVisible: false,
            child: []
        },
        {
            name: "Contact Us",
            link: "/contact-us",
            isChildVisible: false,
            child: []
        },

    ]);

    useEffect(() => {
        if (token && !authLoaded) {
            const json: any = jwtDecode(token);

            setAuth({
                id: json.sub,
                userName: json.username,
                givenname: json.username,
                emailAddress: json.email,
                roles: json.role,
                accessToken: token,
                profileImage: profileImg,
            });

            setAuthLoaded(true);
        }
    }, [token, authLoaded, profileImg, setAuth]);

    useEffect(() => {
        const handleClick = (e: MouseEvent) => {
            if (
                profileDropdownRef.current &&
                !profileDropdownRef.current.contains(e.target as Node)
            ) {
                setShowMenu(false);
            }
        };

        document.addEventListener("click", handleClick);

        return () => {
            document.removeEventListener("click", handleClick);
        };
    }, []);

    const logout = () => {
        setAuth(null);

        [
            "token-velox-charter",
            "role-velox-charter",
            "roleId-velox-charter",
            "userId-velox-charter",
            "userName-velox-charter",
            "fullName-velox-charter",
            "profileImg-velox-charter",
            "expires-velox-charter",
        ].forEach((x) => Cookies.remove(x));

        navigate("/");
    };

    const linkClasses = ({ isActive }: { isActive: boolean }) =>
        `text-sm hover:text-primary ${isActive ? "text-primary font-medium" : "text-blackRgba"
        }`;

    useEffect(() => {
        const handleDropDown = (e: MouseEvent) => {
            if (!mobileViewRef.current?.contains(e.target as Node)) {
                if (!(dropdownRef.current)[1]?.contains(e.target as Node) && !(dropdownRef.current)[2]?.contains(e.target as Node)) {
                    setNavbarHeader((prevNavbar) =>
                        prevNavbar.map((item) => ({
                            ...item,
                            isChildVisible: false
                        }))
                    )
                }
            }
        }

        return () => {
            document.removeEventListener("click", handleDropDown);
        };
    }, [])

    const normalize = (value: string) =>
        value.replace(/\s+/g, "").replace(/-/g, "").toLowerCase();

    const hasAnyAdminSettingsPermission = appAdminSettingRoutes.some((item) => {
        if (isAdmin) return true;

        const parentKey = normalize(item.section);
        if (permissions?.[parentKey]) return true;

        return item.child?.some((child: any) => {
            const childKey = normalize(child.section);
            return permissions?.[childKey] === true;
        });
    });

    const firstAllowedAdminSetting = appAdminSettingRoutes
        .flatMap((item) => item.child || [])
        .find((child) => {
            if (isAdmin) return true;

            const key = normalize(child.section);
            return permissions?.[key] === true;
        });

    const settingsNavigationLink = isAdmin
        ? "/admin/settings/users"
        : firstAllowedAdminSetting?.path || null;

    const showSettingsIcon = isAdmin || hasAnyAdminSettingsPermission;

    return (
        <>
            <nav className="sticky px-4 md:px-[46px] py-[10px] top-0 z-[50] bg-white shadow-sm border-b">
                {!isAdminRoute && (
                    <div className="w-full flex items-center justify-between">


                        <NavLink to="/">
                            <img
                                className="logo-img w-[80px] md:w-[80px] h-auto"
                                src="/images/nav-logo.png"
                                alt="logo"
                            />
                        </NavLink>

                        <div
                            className="hidden lg:flex items-center gap-2 text-primary font-semibold whitespace-nowrap"
                        >
                            <span className="material-symbols-outlined text-[20px]">
                                call
                            </span>

                            <a
                                href="tel:+12817203007"
                                className="hover:text-secondryBlue text-lg"
                            >
                                (281) 720-3007
                            </a>
                        </div>

                        <div className="hidden lg:flex items-center gap-6">
                            {navbarHeader.map((item, index) =>
                                item.child.length === 0 ? (
                                    <NavLink
                                        key={index}
                                        to={item.link}
                                        className={linkClasses}
                                        onClick={() => setSelected(item.link)}
                                    >
                                        {item.name}
                                    </NavLink>
                                ) : (
                                    <div key={index} className="relative group inline-block">
                                        <button className="flex items-center text-sm gap-1 text-blackRgba">
                                            <span className={`text-sm hover:text-primary ${item.child.some((c: any) => c.link === selected)
                                                ? "text-primary font-medium"
                                                : "text-blackRgba"
                                                }`}>
                                                {item.name}
                                            </span>
                                            <span className="material-symbols-outlined">
                                                keyboard_arrow_down
                                            </span>
                                        </button>


                                        <div className="absolute top-full left-0 invisible group-hover:visible opacity-0 group-hover:opacity-100 transition shadow-lg rounded-md z-10 bg-white">
                                            <ul className="min-w-[180px] py-2">
                                                {item.child.map((child: any, i: number) => (
                                                    <li
                                                        key={i}
                                                        className={`px-4 py-2 text-sm hover:bg-gray-100 whitespace-nowrap ${selected === child.link
                                                            ? "text-primary font-medium"
                                                            : "text-blackRgba"
                                                            }`}
                                                    >
                                                        <NavLink
                                                            to={child.link}
                                                            className="block w-full"
                                                            onClick={() => setSelected(child.link)}
                                                        >
                                                            {child.name}
                                                        </NavLink>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    </div>
                                )
                            )}
                        </div>

                        <div className="hidden lg:flex items-center gap-2.5">
                            <button
                                className="px-5 py-[10px] bg-primary text-sm text-white font-medium rounded-[20px] hover:shadow-md hover:opacity-90"
                                onClick={() => setOpenLogin(true)}
                            >
                                Login
                            </button>

                            <button
                                className="px-5 py-[10px] bg-primary text-sm text-white font-medium rounded-[20px] hover:shadow-md hover:opacity-90"
                                onClick={() => navigate("/get-quote")}
                            >
                                Get Your Quote
                            </button>
                        </div>

                        <button
                            className="lg:hidden"
                            onClick={() => setSidebarOpen(!isSidebarOpen)}
                        >
                            <span className="material-symbols-outlined text-3xl">
                                menu
                            </span>
                        </button>
                    </div>
                )}

                {isAdminRoute && (

                    <div className="flex items-center justify-between px-6 h-20">

                        <NavLink to="/admin/dashboard">
                            <img
                                src="/images/nav-logo.png"
                                className="h-14"
                                alt="logo"
                            />
                        </NavLink>

                        {token && (

                            <div
                                ref={profileDropdownRef}
                                className="relative flex items-center gap-4"
                            >
                                 {showSettingsIcon && settingsNavigationLink && (
                                    <button
                                        onClick={() => navigate(settingsNavigationLink)}
                                        className="mr-3"
                                        title="Settings"
                                    >
                                        <span className="material-symbols-outlined text-[28px] text-primary hover:text-secondary">
                                            settings
                                        </span>
                                    </button>
                                )}
                                <button
                                    onClick={() => setShowMenu(!showMenu)}
                                    className="flex items-center gap-3"
                                >
                                    <div
                                        className="cursor-pointer"
                                        onClick={() => setOpenUser(!openUser)}
                                    >
                                        {profileImg !== "" && profileImg !== "null" && profileImg !== "string" ? (
                                            <IconButton aria-label="delete" size="large">
                                                <img
                                                    className="w-[40px] h-[40px] rounded-full"
                                                    alt="profileImg"
                                                    src={`${baseURL}common-files/get/${profileImg}`}
                                                    onError={({ currentTarget }) => {
                                                        currentTarget.onerror = null;
                                                        currentTarget.src = "/images/profile-placeholder.png";
                                                    }}
                                                />
                                            </IconButton>
                                        ) : (
                                            <IconButton
                                                aria-label="delete"
                                                size="large"
                                                sx={{
                                                    fontSize: "32px",
                                                }}
                                            >
                                                <img src="/images/profile-placeholder.png" alt="profile_placeholder" className="w-[40px] h-[40px] rounded-full shadow-box-shadow-profile" />
                                            </IconButton>
                                        )}
                                    </div>

                                    <span className="font-medium">
                                        {auth?.givenname
                                            ? auth.givenname.charAt(0).toUpperCase() + auth.givenname.slice(1)
                                            : ""}
                                    </span>

                                    <span className="material-symbols-outlined">
                                        {showMenu
                                            ? "keyboard_arrow_up"
                                            : "keyboard_arrow_down"}
                                    </span>

                                </button>

                                {showMenu && (

                                    <div className="absolute right-0 top-14 w-44 rounded-lg border bg-white shadow-lg">
                                        <button
                                            onClick={() => {
                                                setProfileOpen(true);
                                                setShowMenu(false);
                                            }}
                                            className="w-full text-left px-4 py-3 hover:bg-gray-100"
                                        >
                                            User Profile
                                        </button>
                                        <button
                                            onClick={() => {
                                                setShowMenu(false);
                                                logout();
                                            }}
                                            className="w-full text-left px-4 py-3 hover:bg-gray-100"
                                        >
                                            Logout
                                        </button>

                                    </div>

                                )}

                            </div>

                        )}

                    </div>

                )}
            </nav>

            {!isAdminRoute && (
                <div className={`fixed top-0 left-0 w-full overflow-auto h-screen overflow-y-auto px-2 bg-white z-50 shadow-lg transform transition-transform duration-300 ease-in-out ${isSidebarOpen ? "translate-x-0" : "-translate-x-full"}`}>
                    <div className="flex items-center justify-between p-4 border-b">
                        <a
                            href="tel:+12817203007"
                            className="flex items-center gap-2 text-primary font-semibold"
                        >
                            <span className="material-symbols-outlined text-[20px]">
                                call
                            </span>

                            <span>(281) 720-3007</span>
                        </a>

                        <button onClick={() => setSidebarOpen(false)}>
                            <span className="material-symbols-outlined text-2xl hover:text-red-500">
                                close
                            </span>
                        </button>
                    </div>


                    <div className="p-4 space-y-2">
                        {navbarHeader.map((item, index) =>
                            item.child.length === 0 ? (
                                <div key={index}>
                                    <NavLink
                                        to={item.link}
                                        className={linkClasses}
                                        onClick={() => {
                                            setSelected(item.link);
                                            setSidebarOpen(false);
                                            setOpenDropdown(false);
                                        }}
                                    >
                                        {item.name}
                                    </NavLink>
                                </div>

                            ) : (
                                <div key={index}>
                                    <button
                                        className={`flex items-center text-sm ${item.child.some((c: any) => c.link === selected)
                                            ? "text-primary font-medium"
                                            : "text-blackRgba"
                                            }`}
                                        onClick={() =>
                                            setOpenDropdown(
                                                openDropdown === index ? null : index
                                            )
                                        }
                                    >
                                        {item.name}
                                        <span className="material-symbols-outlined">
                                            expand_more
                                        </span>
                                    </button>

                                    {openDropdown === index && (
                                        <div className="ml-4 mt-2 flex flex-col gap-2">
                                            {item.child.map((child: any, i: number) => (
                                                <NavLink
                                                    key={i}
                                                    to={child.link}
                                                    className={`px-4 py-2 text-sm whitespace-nowrap ${selected === child.link
                                                        ? "text-primary font-medium"
                                                        : "text-blackRgba"
                                                        }`}
                                                    onClick={() => {
                                                        setSelected(child.link);
                                                        setSidebarOpen(false);
                                                    }}
                                                >
                                                    {child.name}
                                                </NavLink>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )
                        )}
                    </div>

                    <div className="flex flex-col gap-3 mt-4">

                        <button
                            className="w-full px-5 py-[10px] bg-primary text-white rounded-[20px]"
                            onClick={() => {
                                navigate("/get-quote")
                                setSidebarOpen(false);
                            }}
                        >
                            Get Your Quote
                        </button>
                    </div>
                </div>
            )}

            {
                !isAdminRoute &&
                isSidebarOpen && (
                    <div
                        className="fixed inset-0 bg-black bg-opacity-50 z-40"
                        onClick={() => setSidebarOpen(false)}
                    />
                )
            }

            {!isAdminRoute && (
                <Login
                    open={openLogin}
                    onClose={() => setOpenLogin(false)}
                />
            )}
        </>
    )
}